type PostText = { title?: string | null; content: string };

export type FoldedPostPreview = {
  heading: string;
  expandedHeading: string;
  excerpt: string;
  body: string;
  hasAdditionalText: boolean;
};

/**
 * Use a first-person conversational label only when a card is opened.
 * Do not rewrite the original author's saved title or invent details.
 */
export function getFirstPersonConversationHeading(heading: string): string {
  const title = heading.trim();
  if (!title || title === 'Community conversation') return "Here's what I'm sharing";
  if (/^(?:i\b|my\b|we\b|our\b)/i.test(title)) return title;
  return /\?\s*$/.test(title) ? "I'm asking: " + title : "I'm sharing: " + title;
}

/**
 * Fold the post into a short heading and optional excerpt.
 * For posts without a title, the first 140ish characters become the heading;
 * expanding reveals only the remaining characters, never a duplicate caption.
 */
export function getCommunityFoldPreview(post: PostText): FoldedPostPreview {
  const title = post.title?.trim();
  const content = post.content.trim();

  if (title) {
    return {
      heading: title,
      expandedHeading: getFirstPersonConversationHeading(title),
      excerpt: content.replace(/\s+/g, ' ').slice(0, 180).trim(),
      body: content,
      hasAdditionalText: !!content,
    };
  }

  if (!content) {
    return { heading: 'Community conversation', expandedHeading: "Here's what I'm sharing", excerpt: '', body: '', hasAdditionalText: false };
  }

  if (content.length <= 140) {
    return { heading: content, expandedHeading: getFirstPersonConversationHeading(content), excerpt: '', body: '', hasAdditionalText: false };
  }

  let limit = content.lastIndexOf(' ', 140);
  if (limit < 75) limit = 140;
  const heading = content.slice(0, limit).replace(/\s+/g, ' ').trim();
  const body = content.slice(limit).trim();
  return { heading: heading + '…', expandedHeading: getFirstPersonConversationHeading(heading + '…'), excerpt: '', body, hasAdditionalText: !!body };
}
